/* ==========================================================================
   Vildblad — theme.js
   Mjuka interaktioner: header, lådor, varukorg, produktsida, sök, filter.
   Ingen extern kod, allt är vanlig JavaScript.
   ========================================================================== */
(() => {
  'use strict';

  const V = window.Vildblad || {};
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isDesktop = () => window.matchMedia('(min-width: 990px)').matches;
  const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* ---------- Toast ---------- */
  const toast = (() => {
    const el = $('[data-toast]');
    let timer;
    return (text) => {
      if (!el) return;
      $('[data-toast-text]', el).textContent = text;
      el.classList.add('is-visible');
      clearTimeout(timer);
      timer = setTimeout(() => el.classList.remove('is-visible'), 3200);
    };
  })();

  /* ==========================================================================
     Lådor (drawers)
     ========================================================================== */
  const Drawers = (() => {
    let openDrawer = null;
    let lastFocus = null;

    const focusables = (el) => $$('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])', el)
      .filter((n) => n.offsetParent !== null);

    function open(id, opener) {
      const drawer = document.getElementById(id);
      if (!drawer) return;
      if (openDrawer && openDrawer !== drawer) close(openDrawer, false);
      lastFocus = opener || document.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('no-scroll');
      $$(`[aria-controls="${id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
      openDrawer = drawer;
      const panel = $('.drawer__panel', drawer);
      const input = $('[data-search-input]', drawer);
      setTimeout(() => {
        if (input) input.focus({ preventScroll: true });
        else if (panel) panel.focus({ preventScroll: true });
      }, reduceMotion ? 0 : 120);
      drawer.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    function close(drawer = openDrawer, restoreFocus = true) {
      if (!drawer) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      $$(`[aria-controls="${drawer.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      if (openDrawer === drawer) openDrawer = null;
      if (!openDrawer) document.body.classList.remove('no-scroll');
      if (restoreFocus && lastFocus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
      drawer.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }

    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-drawer-open]');
      if (opener) {
        e.preventDefault();
        open(opener.dataset.drawerOpen, opener);
        return;
      }
      const closer = e.target.closest('[data-drawer-close]');
      if (closer) {
        e.preventDefault();
        close(closer.closest('[data-drawer]'));
      }
    });

    document.addEventListener('keydown', (e) => {
      if (!openDrawer) return;
      if (e.key === 'Escape') { close(); return; }
      if (e.key === 'Tab') {
        const items = focusables($('.drawer__panel', openDrawer));
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });

    return { open, close, get current() { return openDrawer; } };
  })();

  /* ==========================================================================
     Header
     ========================================================================== */
  function initHeader() {
    const wrapper = $('[data-header]');
    if (!wrapper) return;
    const section = wrapper.closest('.shopify-section') || wrapper;
    const hideOnScroll = wrapper.hasAttribute('data-hide-on-scroll');
    const backdrop = $('[data-mega-backdrop]');

    // Flytta mobilmenyn till body så att den inte begränsas av headerns lager
    const menuDrawer = $('#MenuDrawer', section);
    if (menuDrawer) {
      $$('body > #MenuDrawer').forEach((n) => n.remove());
      document.body.appendChild(menuDrawer);
    }

    // Genomskinlig header bara om första sektionen är en hero med text över bild
    if (wrapper.hasAttribute('data-overlay')) {
      const first = $('main > .shopify-section:first-child');
      if (first && $('.hero--overlay', first)) document.body.classList.add('has-overlay-header');
      else wrapper.classList.remove('header-wrapper--overlay');
    }

    // Scroll: klasser skrivs bara när läget faktiskt ändras (inga skrivningar per bildruta).
    // Headern göms bara på dator och först efter en tydlig scroll nedåt (hysteres), så att
    // den inte fladdrar vid små rörelser, studs eller när man vänder riktning.
    let lastY = Math.max(0, window.scrollY);
    let anchorY = lastY;
    let dir = 0;
    let hidden = false;
    let scrolled = null;
    let ticking = false;
    const threshold = () => (wrapper.classList.contains('header-wrapper--overlay') ? 40 : 4);
    const setHidden = (value) => {
      if (hidden === value) return;
      hidden = value;
      section.classList.toggle('is-hidden', value);
      document.body.classList.toggle('header-hidden', value);
    };

    const update = () => {
      ticking = false;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const raw = window.scrollY;
      if (raw < 0 || raw > max + 1) return; // gummibandsstuds på iOS
      const y = raw;
      const isScrolled = y > threshold();
      if (isScrolled !== scrolled) { scrolled = isScrolled; wrapper.classList.toggle('is-scrolled', isScrolled); }
      const delta = y - lastY;
      if (delta !== 0) {
        const d = delta > 0 ? 1 : -1;
        if (d !== dir) { dir = d; anchorY = lastY; }
      }
      if (hideOnScroll && isDesktop() && !Drawers.current && !wrapper.classList.contains('is-open')) {
        if (!hidden && dir === 1 && y > 320 && y - anchorY > 60) setHidden(true);
        else if (hidden && ((dir === -1 && anchorY - y > 80) || y < 160)) setHidden(false);
      } else {
        setHidden(false);
      }
      lastY = y;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    window.addEventListener('resize', debounce(update, 150));
    update();

    // Mega-meny med mjuk hover-intention
    const items = $$('[data-mega]', wrapper);
    let openItem = null;
    let closeTimer;
    let openTimer;
    const openMega = (item) => {
      clearTimeout(closeTimer);
      if (openItem === item) return;
      if (openItem) openItem.classList.remove('is-open');
      item.classList.add('is-open');
      $('.main-nav__link', item)?.setAttribute('aria-expanded', 'true');
      wrapper.classList.add('is-open');
      backdrop?.classList.add('is-visible');
      openItem = item;
    };
    const closeMega = () => {
      if (!openItem) return;
      openItem.classList.remove('is-open');
      $('.main-nav__link', openItem)?.setAttribute('aria-expanded', 'false');
      openItem = null;
      wrapper.classList.remove('is-open');
      backdrop?.classList.remove('is-visible');
    };
    items.forEach((item) => {
      item.addEventListener('mouseenter', () => {
        if (!isDesktop()) return;
        clearTimeout(closeTimer);
        clearTimeout(openTimer);
        openTimer = setTimeout(() => openMega(item), openItem ? 0 : 90);
      });
      item.addEventListener('mouseleave', () => {
        clearTimeout(openTimer);
        closeTimer = setTimeout(closeMega, 180);
      });
      item.addEventListener('focusin', () => isDesktop() && openMega(item));
      item.addEventListener('focusout', (e) => { if (!item.contains(e.relatedTarget)) closeTimer = setTimeout(closeMega, 60); });
      const link = $('.main-nav__link', item);
      // Pekskärm på stor skärm: första tryck öppnar panelen
      link?.addEventListener('click', (e) => {
        if (isDesktop() && window.matchMedia('(hover: none)').matches && openItem !== item) { e.preventDefault(); openMega(item); }
      });
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeMega(); });
    backdrop?.addEventListener('click', closeMega);

    // Mobilmeny: undermenyer
    document.addEventListener('click', (e) => {
      const t = e.target.closest('[data-accordion-toggle]');
      if (!t) return;
      const expanded = t.getAttribute('aria-expanded') === 'true';
      t.setAttribute('aria-expanded', String(!expanded));
    });
  }

  /* ---------- Meddelandefält ---------- */
  function initAnnouncement() {
    const bar = $('[data-announcement]');
    if (!bar) return;
    const items = $$('.announcement__item', bar);
    if (items.length < 2) return;
    const split = bar.classList.contains('announcement--split');
    let i = 0;
    let timer;
    const step = () => {
      if (split && isDesktop()) return;
      const cur = items[i];
      i = (i + 1) % items.length;
      const next = items[i];
      cur.classList.add('is-leaving');
      cur.classList.remove('is-active');
      next.classList.add('is-active');
      setTimeout(() => cur.classList.remove('is-leaving'), 800);
    };
    const start = () => { clearInterval(timer); timer = setInterval(step, parseInt(bar.dataset.interval, 10) || 5000); };
    bar.addEventListener('mouseenter', () => clearInterval(timer));
    bar.addEventListener('mouseleave', start);
    start();
  }

  /* ==========================================================================
     Rörelse: inladdning vid scroll, hero, ord-animation
     ========================================================================== */
  const Reveal = (() => {
    const io = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -40px 0px', threshold: 0 }) : null;

    const active = () => document.documentElement.classList.contains('js-reveal');
    const observe = (root = document) => {
      $$('[data-reveal]:not(.is-revealed), [data-reveal-media]:not(.is-revealed)', root).forEach((el) => {
        if (!io || reduceMotion || !active()) { el.classList.add('is-revealed'); return; }
        io.observe(el);
      });
    };
    return { observe };
  })();

  function splitWords(el) {
    if (el.classList.contains('is-split')) return;
    let index = 0;
    const walk = (node) => {
      Array.from(node.childNodes).forEach((child) => {
        if (child.nodeType === 3) {
          const parts = child.textContent.split(/(\s+)/);
          const frag = document.createDocumentFragment();
          parts.forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
            const outer = document.createElement('span');
            outer.className = 'word';
            const inner = document.createElement('span');
            inner.textContent = part;
            inner.style.setProperty('--i', index++);
            outer.appendChild(inner);
            frag.appendChild(outer);
          });
          child.replaceWith(frag);
        } else if (child.nodeType === 1 && child.tagName !== 'BR') {
          walk(child);
        }
      });
    };
    walk(el);
    el.classList.add('is-split');
  }

  function initHeroes() {
    const motion = document.documentElement.classList.contains('js-reveal');
    $$('[data-split]').forEach((el) => {
      if (reduceMotion || !motion) { el.classList.add('is-split'); return; }
      splitWords(el);
      const host = el.closest('[data-hero]') || el.closest('.collection-hero') || el.parentElement;
      if (!host.hasAttribute('data-hero')) requestAnimationFrame(() => requestAnimationFrame(() => host.classList.add('is-loaded')));
    });
    $$('[data-hero]').forEach((hero) => {
      const img = $('.hero__media img', hero);
      const done = () => requestAnimationFrame(() => hero.classList.add('is-loaded'));
      if (!img || img.complete) done();
      else { img.addEventListener('load', done, { once: true }); img.addEventListener('error', done, { once: true }); setTimeout(done, 1600); }
    });
  }

  /* ---------- Dragspel med mjuk höjd ---------- */
  function initAccordions(root = document) {
    $$('details[data-accordion]:not([data-ready])', root).forEach((details) => {
      details.setAttribute('data-ready', '');
      const summary = $('summary', details);
      const content = $('.accordion__content', details);
      if (!summary || !content || reduceMotion) return;
      let anim = null;
      summary.addEventListener('click', (e) => {
        e.preventDefault();
        if (anim) anim.cancel();
        if (details.open) {
          const h = content.offsetHeight;
          anim = content.animate([{ height: `${h}px`, opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 420, easing: 'cubic-bezier(.22,.61,.36,1)' });
          anim.onfinish = () => { details.open = false; anim = null; };
        } else {
          details.open = true;
          const h = content.offsetHeight;
          anim = content.animate([{ height: '0px', opacity: 0 }, { height: `${h}px`, opacity: 1 }], { duration: 520, easing: 'cubic-bezier(.16,1,.3,1)' });
          anim.onfinish = () => { anim = null; };
        }
      });
    });
  }

  function initFooter() {
    if (isDesktop()) return;
    $$('[data-footer-details]').forEach((d) => d.removeAttribute('open'));
  }

  /* ---------- Glidlistor ---------- */
  function initSliders(root = document) {
    $$('[data-slider-section]', root).forEach((section) => {
      const track = $('[data-slider]', section);
      const prev = $('[data-slider-prev]', section);
      const next = $('[data-slider-next]', section);
      if (!track || !prev || !next) return;
      const stepSize = () => {
        const item = track.firstElementChild;
        if (!item) return track.clientWidth;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return item.getBoundingClientRect().width + gap;
      };
      const update = () => {
        prev.disabled = track.scrollLeft < 4;
        next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      };
      prev.addEventListener('click', () => track.scrollBy({ left: -stepSize(), behavior: 'smooth' }));
      next.addEventListener('click', () => track.scrollBy({ left: stepSize(), behavior: 'smooth' }));
      track.addEventListener('scroll', debounce(update, 60), { passive: true });
      window.addEventListener('resize', debounce(update, 150));
      update();
    });
  }

  /* ==========================================================================
     Varukorg
     ========================================================================== */
  const Cart = (() => {
    const drawerSection = 'cart-drawer';

    const pageSection = () => $('[data-cart-page]')?.dataset.sectionId;
    const sectionsToRender = () => {
      const list = [];
      if ($('[data-cart-drawer]')) list.push(drawerSection);
      const page = pageSection();
      if (page) list.push(page);
      return list;
    };

    function setCount(count) {
      $$('[data-cart-count]').forEach((el) => {
        const prev = parseInt(el.dataset.cartCount, 10) || 0;
        el.dataset.cartCount = count;
        el.textContent = count > 0 ? count : '';
        if (count > prev && !reduceMotion) {
          el.classList.remove('is-bumping');
          void el.offsetWidth;
          el.classList.add('is-bumping');
        }
      });
    }

    function render(sections, { newKeys = [] } = {}) {
      if (!sections) return;
      const drawerHTML = sections[drawerSection];
      if (drawerHTML) {
        const doc = parseHTML(drawerHTML);
        const fresh = $('[data-cart-drawer-inner]', doc);
        const current = $('[data-cart-drawer-inner]');
        if (fresh && current) {
          const scroll = $('.drawer__body', current)?.scrollTop || 0;
          current.replaceWith(fresh);
          const body = $('.drawer__body', fresh);
          if (body) body.scrollTop = scroll;
          newKeys.forEach((key) => $(`[data-line-key="${CSS.escape(key)}"]`, fresh)?.classList.add('is-new'));
        }
      }
      const page = pageSection();
      if (page && sections[page]) {
        const doc = parseHTML(sections[page]);
        const fresh = $('[data-cart-page-inner]', doc);
        const current = $('[data-cart-page-inner]');
        if (fresh && current) current.replaceWith(fresh);
      }
    }

    async function refreshCount(cartJson) {
      if (cartJson && typeof cartJson.item_count === 'number') { setCount(cartJson.item_count); return; }
      try {
        const res = await fetch(`${V.routes.cart}.js`, { headers: { Accept: 'application/json' } });
        const cart = await res.json();
        setCount(cart.item_count);
      } catch (e) { /* tyst */ }
    }

    async function add(items, { button } = {}) {
      button?.classList.add('is-loading');
      try {
        const body = { items, sections: sectionsToRender(), sections_url: window.location.pathname };
        const res = await fetch(`${V.routes.cartAdd}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        });
        const data = await res.json();
        if (!res.ok || data.status) {
          throw new Error(data.description || data.message || V.strings.cartError);
        }
        if (V.cartType === 'page' || !$('[data-cart-drawer]')) {
          window.location.href = V.routes.cart;
          return data;
        }
        const keys = (data.items || []).map((i) => i.key);
        render(data.sections, { newKeys: keys });
        await refreshCount();
        if (button) {
          button.classList.remove('is-loading');
          button.classList.add('is-added');
          setTimeout(() => button.classList.remove('is-added'), 1600);
        }
        Drawers.open('CartDrawer', button);
        return data;
      } catch (err) {
        toast(err.message || V.strings.cartError);
        throw err;
      } finally {
        button?.classList.remove('is-loading');
      }
    }

    async function change(key, quantity, lineEl) {
      lineEl?.classList.add(quantity === 0 ? 'is-removing' : 'is-updating');
      try {
        const res = await fetch(`${V.routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity, sections: sectionsToRender(), sections_url: window.location.pathname })
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || V.strings.cartError);
        const delay = quantity === 0 && !reduceMotion ? 260 : 0;
        setTimeout(() => {
          render(data.sections);
          refreshCount(data);
        }, delay);
      } catch (err) {
        lineEl?.classList.remove('is-updating', 'is-removing');
        toast(err.message || V.strings.cartError);
      }
    }

    // Händelser
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-cart-change]');
      if (btn) {
        const line = btn.closest('[data-line-key]');
        if (!line) return;
        e.preventDefault();
        change(line.dataset.lineKey, Math.max(0, parseInt(btn.dataset.cartChange, 10) || 0), line);
        return;
      }
      const opener = e.target.closest('[data-cart-open]');
      if (opener && $('#CartDrawer') && !$('[data-cart-page]')) {
        e.preventDefault();
        Drawers.open('CartDrawer', opener);
      }
    });
    document.addEventListener('change', (e) => {
      const input = e.target.closest('[data-cart-input]');
      if (input) {
        const line = input.closest('[data-line-key]');
        change(line.dataset.lineKey, Math.max(0, parseInt(input.value, 10) || 0), line);
      }
    });
    document.addEventListener('input', debounce((e) => {
      const note = e.target.closest && e.target.closest('[data-cart-note]');
      if (!note) return;
      fetch(`${V.routes.cartUpdate}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: note.value })
      });
    }, 500));

    return { add, change, render, refreshCount };
  })();

  /* ---------- Snabbköp i produktkort ---------- */
  function initQuickAdd() {
    document.addEventListener('click', (e) => {
      const choose = e.target.closest('[data-quick-choose]');
      if (choose) {
        e.preventDefault();
        const card = choose.closest('[data-product-card]');
        $$('[data-product-card].is-choosing').forEach((c) => c !== card && c.classList.remove('is-choosing'));
        card.classList.add('is-choosing');
        choose.setAttribute('aria-expanded', 'true');
        $('.card__variants-list button:not([disabled])', card)?.focus({ preventScroll: true });
        return;
      }
      const closeBtn = e.target.closest('[data-quick-close]');
      if (closeBtn) {
        const card = closeBtn.closest('[data-product-card]');
        card.classList.remove('is-choosing');
        $('[data-quick-choose]', card)?.setAttribute('aria-expanded', 'false');
        return;
      }
      const add = e.target.closest('[data-quick-add]');
      if (add) {
        e.preventDefault();
        const card = add.closest('[data-product-card]');
        const button = card && add.closest('[data-card-variants]') ? $('[data-quick-choose]', card) : add;
        Cart.add([{ id: Number(add.dataset.quickAdd), quantity: 1 }], { button }).then(() => {
          card?.classList.remove('is-choosing');
        }).catch(() => {});
        return;
      }
      // Klick utanför stänger variantväljaren
      if (!e.target.closest('[data-card-variants]')) {
        $$('[data-product-card].is-choosing').forEach((c) => c.classList.remove('is-choosing'));
      }
    });
  }

  /* ==========================================================================
     Produktsida
     ========================================================================== */
  function initProduct(section) {
    const jsonEl = $('[data-product-json]', section);
    if (!jsonEl) return;
    const product = JSON.parse(jsonEl.textContent);
    const form = $('[data-product-form]', section);
    const idInput = $('[data-variant-id]', section);
    const addBtn = $('[data-add-button]', section);
    const stickyBtn = $('[data-sticky-add]', section);
    const gallery = $('[data-gallery-list]', section);
    const dots = $$('[data-gallery-dots] span', section);
    const errorEl = $('[data-form-error]', section);

    const selectedOptions = () => {
      const picker = $('[data-variant-picker]', section);
      if (!picker) return null;
      return $$('fieldset', picker).map((fs) => $('input:checked', fs)?.value);
    };

    const findVariant = (opts) => product.variants.find((v) => v.options.every((o, i) => o === opts[i]));

    const setButton = (variant) => {
      const text = !variant ? V.strings.unavailable : variant.available ? V.strings.addToCart : V.strings.soldOut;
      [addBtn, stickyBtn].forEach((b) => {
        if (!b) return;
        b.disabled = !variant || !variant.available;
        const t = $('[data-add-text]', b);
        if (t) t.textContent = text;
      });
    };

    const updateAvailability = (opts) => {
      const picker = $('[data-variant-picker]', section);
      if (!picker) return;
      $$('fieldset', picker).forEach((fs, idx) => {
        $$('input', fs).forEach((input) => {
          const test = opts.slice();
          test[idx] = input.value;
          const match = product.variants.find((v) => v.options.every((o, i) => o === test[i]));
          input.classList.toggle('is-unavailable', !match || !match.available);
        });
      });
    };

    const goToMedia = (mediaId) => {
      if (!gallery || !mediaId) return;
      const item = $(`[data-media-id="${mediaId}"]`, gallery);
      if (!item) return;
      if (isDesktop()) {
        if (gallery.firstElementChild !== item) {
          item.style.opacity = '0';
          gallery.prepend(item);
          requestAnimationFrame(() => { item.style.transition = 'opacity .6s ease'; item.style.opacity = '1'; });
        }
      } else {
        gallery.scrollTo({ left: item.offsetLeft - gallery.offsetLeft - parseFloat(getComputedStyle(gallery).paddingLeft || 0), behavior: reduceMotion ? 'auto' : 'smooth' });
      }
    };

    const onVariantChange = () => {
      const opts = selectedOptions();
      if (!opts) return;
      const variant = findVariant(opts);
      $$('[data-option-value]', section).forEach((el, i) => { el.textContent = opts[i]; });
      updateAvailability(opts);
      setButton(variant);
      section.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: variant || null, product } }));
      if (!variant) return;
      idInput.value = variant.id;
      const priceWrap = $('[data-product-price] [data-price]', section);
      if (priceWrap) {
        const onSale = variant.compare_at_price > variant.price;
        priceWrap.classList.toggle('price--sale', onSale);
        $('[data-price-current]', priceWrap).textContent = variant.price_formatted;
        const cmp = $('[data-price-compare]', priceWrap);
        if (cmp) { cmp.textContent = variant.compare_formatted; cmp.hidden = !onSale; }
        const unit = $('[data-price-unit]', priceWrap);
        if (unit && variant.unit_price) unit.textContent = variant.unit_price;
      }
      const sv = $('[data-sticky-variant]', section);
      if (sv) sv.innerHTML = `${variant.title} · <span data-sticky-price>${variant.price_formatted}</span>`;
      if (variant.featured_media_id) goToMedia(variant.featured_media_id);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    };

    $('[data-variant-picker]', section)?.addEventListener('change', onVariantChange);
    if ($('[data-variant-picker]', section)) updateAvailability(selectedOptions());

    // Antal
    $$('[data-qty]', section).forEach((q) => {
      const input = $('[data-qty-input]', q);
      $('[data-qty-minus]', q)?.addEventListener('click', () => { input.value = Math.max(1, (parseInt(input.value, 10) || 1) - 1); });
      $('[data-qty-plus]', q)?.addEventListener('click', () => { input.value = (parseInt(input.value, 10) || 1) + 1; });
    });

    // Lägg i varukorgen
    form?.addEventListener('submit', (e) => {
      e.preventDefault();
      if (errorEl) errorEl.hidden = true;
      const qty = parseInt($('[data-qty-input]', section)?.value, 10) || 1;
      Cart.add([{ id: Number(idInput.value), quantity: qty }], { button: addBtn }).catch((err) => {
        if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; }
      });
    });

    // Fast köpknapp
    const sticky = $('[data-sticky-buy]', section);
    const buyArea = $('[data-buy-area]', section);
    if (sticky && buyArea && 'IntersectionObserver' in window) {
      const footer = $('.site-footer');
      let pastBuy = false;
      let nearFooter = false;
      const sync = () => {
        const show = pastBuy && !nearFooter;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', String(!show));
        if (stickyBtn) stickyBtn.tabIndex = show ? 0 : -1;
      };
      new IntersectionObserver(([entry]) => {
        pastBuy = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sync();
      }).observe(buyArea);
      if (footer) new IntersectionObserver(([entry]) => { nearFooter = entry.isIntersecting; sync(); }).observe(footer);
      stickyBtn?.addEventListener('click', () => {
        if (form.requestSubmit) form.requestSubmit(); else form.dispatchEvent(new Event('submit', { cancelable: true }));
      });
    }

    // Galleri: prickar på mobil
    if (gallery && dots.length) {
      gallery.addEventListener('scroll', debounce(() => {
        const items = $$('.gallery__item', gallery);
        const center = gallery.scrollLeft + gallery.clientWidth / 2;
        let idx = 0;
        items.forEach((it, i) => { if (it.offsetLeft - gallery.offsetLeft <= center) idx = i; });
        dots.forEach((d, i) => d.classList.toggle('is-active', i === idx));
      }, 40), { passive: true });
    }

    // Zoom
    const lightbox = $('[data-lightbox]');
    if (lightbox) {
      const img = $('[data-lightbox-img]', lightbox);
      const closeLb = () => { lightbox.classList.remove('is-open'); lightbox.setAttribute('aria-hidden', 'true'); document.body.classList.remove('no-scroll'); };
      section.addEventListener('click', (e) => {
        const z = e.target.closest('[data-zoom]');
        if (!z) return;
        img.src = z.dataset.zoom;
        img.alt = $('img', z)?.alt || '';
        lightbox.classList.add('is-open');
        lightbox.setAttribute('aria-hidden', 'false');
        document.body.classList.add('no-scroll');
      });
      lightbox.addEventListener('click', closeLb);
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && lightbox.classList.contains('is-open')) closeLb(); });
    }
  }

  /* ---------- Rekommendationer ---------- */
  function initRecommendations() {
    $$('[data-recommendations]').forEach((el) => {
      if (!el.dataset.url || el.children.length) return;
      const load = async () => {
        try {
          const res = await fetch(el.dataset.url);
          const doc = parseHTML(await res.text());
          const fresh = $('[data-recommendations]', doc);
          if (fresh && fresh.innerHTML.trim()) {
            el.innerHTML = fresh.innerHTML;
            Reveal.observe(el);
          } else {
            el.closest('.shopify-section')?.setAttribute('hidden', '');
          }
        } catch (e) { /* tyst */ }
      };
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { io.disconnect(); load(); } }, { rootMargin: '0px 0px 400px 0px' });
        io.observe(el);
      } else load();
    });
  }

  /* ==========================================================================
     Sök
     ========================================================================== */
  function initSearch() {
    const drawer = $('[data-search-drawer]');
    if (!drawer) return;
    const input = $('[data-search-input]', drawer);
    const results = $('[data-search-results]', drawer);
    const defaults = $('[data-search-default]', drawer);
    let controller;

    const search = debounce(async () => {
      const q = input.value.trim();
      if (q.length < 2) {
        results.innerHTML = '';
        defaults.hidden = false;
        return;
      }
      controller?.abort();
      controller = new AbortController();
      try {
        const url = `${V.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product,page,article,collection&resources[limit]=10&resources[options][unavailable_products]=last&section_id=predictive-search`;
        const res = await fetch(url, { signal: controller.signal });
        const doc = parseHTML(await res.text());
        const fresh = $('[data-predictive]', doc);
        results.innerHTML = fresh ? fresh.outerHTML : `<p class="muted">${V.strings.noResults}</p>`;
        defaults.hidden = true;
      } catch (e) { /* avbruten */ }
    }, 220);

    input.addEventListener('input', search);
    drawer.addEventListener('drawer:close', () => { controller?.abort(); });
  }

  /* ==========================================================================
     Kollektion: filter, sortering, visa fler
     ========================================================================== */
  function initCollection() {
    const root = $('[data-collection]');
    if (!root) return;
    const sectionId = root.dataset.sectionId;

    const fetchSection = async (url) => {
      const u = new URL(url, window.location.origin);
      u.searchParams.set('section_id', sectionId);
      const res = await fetch(u.toString());
      return parseHTML(await res.text());
    };

    const refresh = async (url, { push = true } = {}) => {
      const results = $('[data-collection-results]', root);
      results?.classList.add('is-loading');
      try {
        const doc = await fetchSection(url);
        const fresh = $('[data-collection]', doc);
        if (!fresh) { window.location.href = url; return; }
        const wasOpen = Drawers.current && Drawers.current.id === 'FilterDrawer';
        if (wasOpen) Drawers.close(undefined, false);
        // Byt ut allt utom hero-delen för att undvika att rubriken animeras igen
        ['.collection__body', '#FilterDrawer'].forEach((sel) => {
          const a = $(sel, root);
          const b = $(sel, fresh);
          if (a && b) a.replaceWith(b);
        });
        if (push) window.history.pushState({ collection: true }, '', url);
        Reveal.observe(root);
        initAccordions(root);
        const top = $('.collection__body', root).getBoundingClientRect().top + window.scrollY - 90;
        if (window.scrollY > top) window.scrollTo({ top, behavior: reduceMotion ? 'auto' : 'smooth' });
      } catch (e) {
        window.location.href = url;
      }
    };

    root.addEventListener('change', (e) => {
      const sort = e.target.closest('[data-sort-select]');
      if (sort) {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', sort.value);
        url.searchParams.delete('page');
        refresh(url.toString());
      }
    });

    root.addEventListener('submit', (e) => {
      const form = e.target.closest('[data-filter-form]');
      if (!form) return;
      e.preventDefault();
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => { if (value !== '') params.append(key, value); });
      refresh(`${form.getAttribute('action')}?${params.toString()}`);
    });

    root.addEventListener('click', async (e) => {
      const link = e.target.closest('[data-filter-link]');
      if (link) {
        e.preventDefault();
        refresh(link.href);
        return;
      }
      const more = e.target.closest('[data-load-more]');
      if (more) {
        e.preventDefault();
        more.classList.add('is-loading');
        try {
          const doc = await fetchSection(more.href);
          const grid = $('[data-product-grid]', root);
          const newItems = $$('[data-product-grid] > li', doc);
          newItems.forEach((li, i) => { li.style.setProperty('--reveal-i', i % 4); grid.appendChild(li); });
          const wrap = $('[data-load-more-wrap]', root);
          const freshWrap = $('[data-load-more-wrap]', doc);
          if (wrap && freshWrap) wrap.replaceWith(freshWrap); else wrap?.remove();
          Reveal.observe(grid);
          window.history.replaceState({}, '', more.href);
        } catch (err) {
          window.location.href = more.href;
        }
      }
    });

    window.addEventListener('popstate', (e) => { if (e.state && e.state.collection) refresh(window.location.href, { push: false }); });
  }

  /* ==========================================================================
     Start
     ========================================================================== */
  function init() {
    initHeader();
    initAnnouncement();
    initHeroes();
    Reveal.observe();
    initAccordions();
    initFooter();
    initSliders();
    initQuickAdd();
    $$('[data-product-section]').forEach(initProduct);
    initRecommendations();
    initSearch();
    initCollection();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Temaredigeraren: initiera om sektioner som laddas om
  document.addEventListener('shopify:section:load', (e) => {
    Reveal.observe(e.target);
    initAccordions(e.target);
    initSliders(e.target);
    $$('[data-split]', e.target).forEach((el) => { el.classList.remove('is-split'); });
    initHeroes();
    $$('[data-product-section]', e.target).forEach(initProduct);
    if ($('[data-header]', e.target)) initHeader();
  });
  document.addEventListener('shopify:section:select', (e) => {
    $$('[data-reveal], [data-reveal-media]', e.target).forEach((el) => el.classList.add('is-revealed'));
  });

  window.Vildblad = Object.assign(V, { Cart, Drawers, toast });
})();